const invert = require('lodash/invert');
const uuidv3 = require('uuid/v3');
const { carmotProviderEnabled } = require('~/config/editions/ClientSafeEditions');
const { findEditionIdFromHostname } = require('@huffpost/interface/dist/client');

const CarmotNamespaceToProviderIdMap = {
  b: 'huffpost_us_3rd_party_674',
  n: 'the_huffington_post_584',
  l: 'huffpost_life_308',
  br: 'huffpost_br_627',
  ca: 'huffpost_ca_629',
  es: 'huffpost_es_19',
  fr: 'huffingtonpost',
  in: 'huffpost_india_124',
  jp: 'huffpost_japan_567',
  it: 'huffpost_it_483',
  kr: 'huffpost_kr_133',
  qc: 'huffpost_qu_bec_18',
  uk: 'huffpost_uk_744',
  gr: 'huffpost_gr_317',
  spoilsuk: 'the_spoils_379',
};
const ProviderIdToCarmotNamespaceMap = invert(CarmotNamespaceToProviderIdMap);

const getCarmotNamespace = (providerId) => ProviderIdToCarmotNamespaceMap[providerId] || 'n';

const getProviderId = (carmotNamespace) => CarmotNamespaceToProviderIdMap[carmotNamespace] || CarmotNamespaceToProviderIdMap.n;

// NB: this logic needs to remain in place until the content team has
// done a complete backfill of all historical huffpost content into
// Carmot with the correct provider_ids!  status as of 7 february 2019:
//   - all life content imported:
//     - content published before 2019-01-22T20:53:00.000Z under 'news' provider
//     - content published after 2019-01-22T20:53:00.000Z under 'life' provider
//   - all news content imported under 'news' provider
//   - some opinion vertical content imported under deprecated 'opinion' provider (not active in cambria)
//   - newer contributor content imported under 'blog' provider:
//     - content published after 2019-01-31T18:00:00.000Z under 'blog' provider
//     - not clear on older blog/contributor content

const lifeProviderCutoverDate = new Date('2019-01-22T20:53:00.000Z');
// const blogContributorDate = new Date('2019-01-31T18:00:00.000Z');

const getProviderShortIdFromEntryData = (entry) => {
  if (carmotProviderEnabled(entry.cmsEdition)) {
    if (entry.cmsEdition === 'us') {
      // Custom logic for US
      const firstPublishDate = new Date(entry.firstPublishDate);
      if (entry.isLifeDepartment()) {
        if (firstPublishDate >= lifeProviderCutoverDate) {
          return 'l';
        }
        if (entry.entryType === 'blog') {
          return 'b';
        }
        return 'n';
      } if (entry.entryType === 'news') {
        return 'n';
      } if (entry.entryType === 'blog') {
        return 'b';
      }
    }

    // TODO: Put logic for other editions here

    return null;
  }

  return entry.cmsEdition;
};

const getProviderIdFromEntryData = (entry) => getProviderId(getProviderShortIdFromEntryData(entry));

const isProviderCorrect = (providerId, entry) => providerId === getProviderIdFromEntryData(entry);

const generateUUID = (providerId, guid) => {
  const transformKeys = [uuidv3.URL, providerId, guid];
  return transformKeys.reduce((namespace, name) => uuidv3(name, namespace), uuidv3.URL);
};

const getEntryInfoFromUrl = (entryUrl) => {
  let hostname;
  let pathname = '';

  try {
    ({ hostname, pathname } = new URL(entryUrl));
  } catch (e) { } // eslint-disable-line no-empty

  const regex = /[a-z0-9-]+\/([a-z0-9-]*)_(a|b|l|n|[a-z]{2})_([a-f0-9]{1,24})/;
  const matches = pathname.match(regex);
  if (matches && matches.length) {
    let providerShortId = matches[2]; // eslint-disable-line prefer-destructuring
    const entryId = matches[3]; // eslint-disable-line prefer-destructuring

    const editionId = findEditionIdFromHostname(hostname);

    // If entryId contains only digits, that means the entry is an MT or AMP entry
    if (entryId.match(/^[0-9]+$/)) {
      // Specific provider IDs for _b_ and _n_ only applies to US.
      // On INTL editions, entries having _b_ or _n_ as providerShortIds are old AMP and MT entries and currently fall under one provider.
      // If providerShortId is _b_ or _n_; and editionId is not US, use editionId as providerShortId
      if (['b', 'n'].includes(providerShortId) && editionId !== 'us') {
        providerShortId = editionId;
      }
    }

    // Try to get provider ID for the extracted providerShortId.
    let providerId = CarmotNamespaceToProviderIdMap[providerShortId];

    // If we cannot find the provider ID from the map, use to the provider ID for editionId.
    if (!providerId) {
      providerId = CarmotNamespaceToProviderIdMap[editionId];
    }

    // If we still cannot find the provider ID from the map, default to the provider ID for US `n` provider.
    if (!providerId) {
      providerId = CarmotNamespaceToProviderIdMap.n;
    }

    return [providerId, entryId];
  }

  return [null, null];
};

const getCarmotUUIDFromUrl = (url) => {
  const [providerId, entryId] = getEntryInfoFromUrl(url);
  if (providerId && entryId) {
    return generateUUID(providerId, entryId);
  }
  return null;
};

export {
  generateUUID, getCarmotNamespace, getCarmotUUIDFromUrl, getEntryInfoFromUrl, getProviderId, getProviderIdFromEntryData, getProviderShortIdFromEntryData, isProviderCorrect,
};

